import React from 'react'
import styled from 'styled-components'
import kebabCase from 'lodash/kebabCase'
import { PortableText, toPlainText } from '@portabletext/react'

import {
  BLOG_TAGS,
  ASK_ANVIL_TAGS,
  types as sanityFieldTypes,
} from '@anvilco-pkg/sanity'

// TODO: use UniversalLink instead....
import Link from 'components/Link'
import Code from 'components/Code'

import { ContentGIFVideo, ContentTable } from './components'

import { getPagePath } from 'helpers/path'
import { BLOG_SLUG, ASK_ANVIL_SLUG } from 'consts'

const StyledHeading = styled.h2`
  .anchor.before {
    position: absolute;
    top: 0;
    left: 0;
    transform: translateX(-100%);
    padding-right: 4px;
  }
  .anchor.after {
    display: inline-block;
    padding-left: 4px;
  }

  .anchor svg {
    visibility: hidden;
  }
  &:hover .anchor svg,
  .anchor:focus svg {
    visibility: visible;
  }
`

const getDownedHeadingFormat = (style) => {
  switch (style) {
    case 'h1':
      return 'h2'
    case 'h2':
      return 'h3'
    case 'h3':
      return 'h4'
    case 'h4':
      return 'h5'
    case 'h5':
      return 'h6'
    case 'h6':
      throw new Error(
        'h6 is not supported, the lowest heading level possible is h5'
      )
    default:
      return 'h6'
  }
}

const headingHandler = ({ children, value, node, ...others }) => {
  const slug = kebabCase(toPlainText(value))

  const anchor = (
    <a className="anchor before" href={`#${slug}`}>
      <svg
        aria-hidden="true"
        focusable="false"
        height="16"
        version="1.1"
        viewBox="0 0 16 16"
        width="16"
      >
        <path
          fillRule="evenodd"
          d="M4 9h1v1H4c-1.5 0-3-1.69-3-3.5S2.55 3 4 3h4c1.45 0 3 1.69 3 3.5 0 1.41-.91 2.72-2 3.25V8.59c.58-.45 1-1.27 1-2.09C10 5.22 8.98 4 8 4H4c-.98 0-2 1.22-2 2.5S3 9 4 9zm9-3h-1v1h1c1 0 2 1.22 2 2.5S13.98 12 13 12H9c-.98 0-2-1.22-2-2.5 0-.83.42-1.64 1-2.09V6.25c-1.09.53-2 1.84-2 3.25C6 11.31 7.55 13 9 13h4c1.45 0 3-1.69 3-3.5S14.5 6 13 6z"
        />
      </svg>
    </a>
  )

  return (
    <StyledHeading
      as={getDownedHeadingFormat(node.style)}
      id={slug}
      style={{ position: 'relative' }}
    >
      {anchor}
      {children}
    </StyledHeading>
  )
}

const validateLink = (children) => {
  if (children?.props?.markType)
    throw new Error('Link contains a nested link; please only link text once.')
}

const getPortableTextComponents = (renderSanityTypeAs, opts) => ({
  // types keys is for any sanity object that is relevant in the content editor.
  types: {
    imageWithCaption: ({ value, ...others }) => {
      const removeShadow = value?.removeShadow
      const src = value?.image?.asset?.url
      const alt = value?.alt || value?.caption || value?.image?.asset?.filename

      if (!src) {
        console.warn('No URL found for image. Defaulting to no image shown.')
      }

      // this class name comes from 'src/components/Article.js'
      const styleProps = removeShadow ? { className: 'noImgBoxShadow' } : {}

      return (
        <>
          {src && (
            <img
              src={src}
              alt={alt}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen
              {...styleProps}
            />
          )}
          {value?.caption && <em>{value.caption}</em>}
        </>
      )
    },
    embeddedUrl: ({ value, ...others }) => {
      return (
        <>
          <iframe
            width="100%"
            height={value?.height}
            src={value?.url}
            style={{ border: 'none', outline: 'none' }}
            allowfullscreen
            allow="fullscreen; picture-in-picture"
          />
          {value?.caption && <em>{value.caption}</em>}
        </>
      )
    },
    gifVideo: ({ value, ...others }) => {
      return (
        <>
          <ContentGIFVideo src={value?.video?.asset?.url} />
          {value?.caption && <em>{value.caption}</em>}
        </>
      )
    },
    code: ({ value, ...others }) => {
      // TODO: would be nicer to specify the title of the content that throws
      // these errors.
      if (!value?.code) {
        throw new Error('Code block does not contain any code.')
      }
      return (
        <Code languageOrLanguageAliases={value?.language}>{value?.code}</Code>
      )
    },
    customTable: ({ value }) => {
      if (!value?.table?.rows) {
        throw new Error('Table block does not contain any row.')
      }
      return (
        <ContentTable
          rows={value.table.rows}
          headersInFirstColumn={value.headersInFirstColumn}
        />
      )
    },
  },
  block: {
    h1: headingHandler,
    h2: headingHandler,
    h3: headingHandler,
    h4: headingHandler,
    h5: headingHandler,
    h6: headingHandler,

    normal: ({ children, node, value, ...others }) => {
      // NOTE: this is done for reference types.
      if (node?._type === sanityFieldTypes?.[renderSanityTypeAs?.type]) {
        return renderSanityTypeAs?.render({ value })
      }

      // TODO: probably a better way to get quotes in there...
      if (opts?.withQuotes) {
        return <p>"{children}"</p>
      }

      return <p>{children}</p>
    },
  },

  marks: {
    internalLink: ({ children, value, ...others }) => {
      // TODO: should probably be a better way to handle this.
      // this should handle ALL internal links, not just blog and ask-anvil links.
      if (!value?.reference) {
        throw new Error(
          'Internal link does not contain a reference. Please add a reference to the link.'
        )
      }
      validateLink(children)
      const valueTag =
        value.reference?.article?.primaryTag ||
        value.reference?.article?.tags[0]

      const blogTag = BLOG_TAGS.find(({ key }) => key === valueTag)
      const foundTag =
        blogTag || ASK_ANVIL_TAGS.find(({ key }) => key === valueTag)

      const to = getPagePath({
        prefixSlug: blogTag ? BLOG_SLUG : ASK_ANVIL_SLUG,
        tagSlug: kebabCase(foundTag.value),
        pageSlug: value.reference.article.slug,
      })

      return <Link to={to}>{children}</Link>
    },
    marketingSiteLink: ({ children, value }) => {
      validateLink(children)
      // if the content author doesnt know the diff b/w a URL and a path, strip the domain.
      // but also tell them to stop using URLs.
      let path = value?.path
      if (!path) {
        throw new Error('Marketing site link does not contain a path.')
      }
      if (path.includes('https://')) {
        console.warn(
          `Internal link from Sanity is a full URL: ${value?.href}. Please use a path instead.`
        )
        path = path.replace('https://', '')
        // use regex to match the path after the domain.
        path = path.match(/(\/.*)/)?.[0]
      }
      return <Link to={value.path}>{children}</Link>
    },
    link: ({ children, value }) => {
      validateLink(children)
      if (!value?.href) {
        throw new Error(
          'Link does not contain a href. Please add a href to the link.'
        )
      }
      const target = value?.blank ? { target: '_blank' } : {}
      return (
        <Link to={value.href} {...target}>
          {children}
        </Link>
      )
    },
  },
})

export const getPlainText = (content) => {
  return toPlainText(content)
}

// NOTE: should make renderSanityTypeAs a list of types to render as.
export default function getContentElement ({
  content,
  renderSanityTypeAs,
  opts,
}) {
  return (
    <PortableText
      value={content}
      components={getPortableTextComponents(renderSanityTypeAs, opts)}
    />
  )
}
